import type { TFunction } from 'i18next'
import type { AppNotificationRecord } from '@/types/notifications'

export interface LocalizedNotification {
  title: string
  message: string
}

interface ContentDef {
  titleKey: string | ((m: Record<string, unknown>) => string)
  messageKey: string | ((m: Record<string, unknown>) => string)
  titleVars?: (m: Record<string, unknown>) => Record<string, string | number>
  messageVars?: (m: Record<string, unknown>) => Record<string, string | number>
}

function s(m: Record<string, unknown>, key: string, fallback = ''): string {
  const v = m[key]
  if (v === null || v === undefined) return fallback
  return String(v)
}

function n(m: Record<string, unknown>, key: string, fallback = 0): number {
  const v = m[key]
  if (v === null || v === undefined) return fallback
  const num = Number(v)
  return Number.isNaN(num) ? fallback : num
}

function rewardLabel(m: Record<string, unknown>): string {
  return (
    s(m, 'requested_reward_title') ||
    (n(m, 'cash_amount') ? `${n(m, 'cash_amount')} ${s(m, 'currency')}` : '') ||
    'reward'
  )
}

const contentMap: Record<string, ContentDef> = {
  /* ─── Agent lifecycle ─── */
  agent_invited: {
    titleKey: (m) =>
      m.activation_url
        ? 'notifications.events.agent_invited.title_agent'
        : 'notifications.events.agent_invited.title_owner',
    messageKey: (m) =>
      m.activation_url
        ? 'notifications.events.agent_invited.message_agent'
        : 'notifications.events.agent_invited.message_owner',
    titleVars: (m) => ({
      inviter: s(m, 'inviter_name', 'Someone'),
      business: s(m, 'business_name', 'the platform'),
      agent: s(m, 'agent_name', 'An agent'),
    }),
    messageVars: () => ({}),
  },
  agent_activation: {
    titleKey: (m) =>
      m.actor_name
        ? 'notifications.events.agent_activation.title_owner'
        : 'notifications.events.agent_activation.title_agent',
    messageKey: (m) =>
      m.actor_name
        ? 'notifications.events.agent_activation.message_owner'
        : 'notifications.events.agent_activation.message_agent',
    titleVars: (m) => ({
      business: s(m, 'business_name', 'the platform'),
      actor: s(m, 'actor_name', 'An agent'),
    }),
    messageVars: (m) => ({
      business: s(m, 'business_name', 'the platform'),
      actor: s(m, 'actor_name', 'An agent'),
    }),
  },
  agent_suspended: {
    titleKey: 'notifications.events.agent_suspended.title',
    messageKey: (m) =>
      s(m, 'reason')
        ? 'notifications.events.agent_suspended.message_with_reason'
        : 'notifications.events.agent_suspended.message',
    titleVars: () => ({}),
    messageVars: (m) => ({
      reason: s(m, 'reason'),
    }),
  },
  agent_reactivated: {
    titleKey: 'notifications.events.agent_reactivated.title',
    messageKey: 'notifications.events.agent_reactivated.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },

  /* ─── Program lifecycle ─── */
  program_assigned: {
    titleKey: 'notifications.events.program_assigned.title',
    messageKey: 'notifications.events.program_assigned.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },
  program_unassigned: {
    titleKey: 'notifications.events.program_unassigned.title',
    messageKey: 'notifications.events.program_unassigned.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },
  program_activated: {
    titleKey: 'notifications.events.program_activated.title',
    messageKey: 'notifications.events.program_activated.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },
  program_paused: {
    titleKey: 'notifications.events.program_paused.title',
    messageKey: 'notifications.events.program_paused.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },
  program_resumed: {
    titleKey: 'notifications.events.program_resumed.title',
    messageKey: 'notifications.events.program_resumed.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },
  program_suspended: {
    titleKey: 'notifications.events.program_suspended.title',
    messageKey: (m) =>
      s(m, 'suspension_deadline_at')
        ? 'notifications.events.program_suspended.message_with_deadline'
        : 'notifications.events.program_suspended.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: (m) => ({
      business: s(m, 'business_name', 'Business'),
    }),
  },
  program_rewards_updated: {
    titleKey: 'notifications.events.program_rewards_updated.title',
    messageKey: (m) =>
      s(m, 'exchange_pack_name')
        ? 'notifications.events.program_rewards_updated.message_with_pack'
        : 'notifications.events.program_rewards_updated.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: (m) => ({ pack: s(m, 'exchange_pack_name') }),
  },
  program_archived: {
    titleKey: 'notifications.events.program_archived.title',
    messageKey: 'notifications.events.program_archived.message',
    titleVars: (m) => ({ program: s(m, 'program_name', 'Program') }),
    messageVars: () => ({}),
  },

  /* ─── Business lifecycle ─── */
  business_invited: {
    titleKey: 'notifications.events.business_invited.title',
    messageKey: 'notifications.events.business_invited.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  business_approved: {
    titleKey: 'notifications.events.business_approved.title',
    messageKey: 'notifications.events.business_approved.message',
    titleVars: (m) => ({ business: s(m, 'business_name', 'Business') }),
    messageVars: () => ({}),
  },
  business_rejected: {
    titleKey: 'notifications.events.business_rejected.title',
    messageKey: 'notifications.events.business_rejected.message',
    titleVars: (m) => ({ business: s(m, 'business_name', 'Business') }),
    messageVars: () => ({}),
  },

  /* ─── Prospect lifecycle ─── */
  prospect_submitted: {
    titleKey: 'notifications.events.prospect_submitted.title',
    messageKey: 'notifications.events.prospect_submitted.message',
    titleVars: (m) => ({ agent: s(m, 'agent_name', 'An agent') }),
    messageVars: (m) => ({
      prospect: s(m, 'contact_name', 'A prospect'),
      company: s(m, 'company_name', 'No company'),
      program: s(m, 'program_name', 'Program'),
    }),
  },
  prospect_submitted_public: {
    titleKey: 'notifications.events.prospect_submitted_public.title',
    messageKey: 'notifications.events.prospect_submitted_public.message',
    titleVars: () => ({}),
    messageVars: (m) => ({
      prospect: s(m, 'contact_name', 'A prospect'),
      company: s(m, 'company_name', 'No company'),
      program: s(m, 'program_name', 'Program'),
      source: s(m, 'agent_name', 'Public form'),
    }),
  },
  prospect_status_changed: {
    titleKey: 'notifications.events.prospect_status_changed.title',
    messageKey: 'notifications.events.prospect_status_changed.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  prospect_converted: {
    titleKey: 'notifications.events.prospect_converted.title',
    messageKey: 'notifications.events.prospect_converted.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  prospect_lost: {
    titleKey: 'notifications.events.prospect_lost.title',
    messageKey: 'notifications.events.prospect_lost.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },

  /* ─── Transaction lifecycle ─── */
  transaction_detected: {
    titleKey: 'notifications.events.transaction_detected.title',
    messageKey: 'notifications.events.transaction_detected.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  transaction_validated: {
    titleKey: 'notifications.events.transaction_validated.title',
    messageKey: 'notifications.events.transaction_validated.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  transaction_paid: {
    titleKey: 'notifications.events.transaction_paid.title',
    messageKey: 'notifications.events.transaction_paid.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },

  /* ─── Points ─── */
  points_earned: {
    titleKey: 'notifications.events.points_earned.title',
    messageKey: 'notifications.events.points_earned.message',
    titleVars: () => ({}),
    messageVars: (m) => ({ points: n(m, 'points_amount') }),
  },

  /* ─── Exchange lifecycle ─── */
  exchange_requested: {
    titleKey: 'notifications.events.exchange_requested.title',
    messageKey: 'notifications.events.exchange_requested.message',
    titleVars: (m) => ({ agent: s(m, 'agent_name', 'An agent') }),
    messageVars: (m) => ({
      points: n(m, 'points_amount'),
      reward: rewardLabel(m),
    }),
  },
  exchange_approved: {
    titleKey: 'notifications.events.exchange_approved.title',
    messageKey: 'notifications.events.exchange_approved.message',
    titleVars: () => ({}),
    messageVars: (m) => ({
      points: n(m, 'points_amount'),
      reward: rewardLabel(m),
    }),
  },
  exchange_rejected: {
    titleKey: 'notifications.events.exchange_rejected.title',
    messageKey: 'notifications.events.exchange_rejected.message',
    titleVars: () => ({}),
    messageVars: (m) => ({ points: n(m, 'points_amount') }),
  },
  exchange_processing: {
    titleKey: 'notifications.events.exchange_processing.title',
    messageKey: 'notifications.events.exchange_processing.message',
    titleVars: () => ({}),
    messageVars: (m) => ({ reward: rewardLabel(m) }),
  },
  exchange_completed: {
    titleKey: 'notifications.events.exchange_completed.title',
    messageKey: 'notifications.events.exchange_completed.message',
    titleVars: () => ({}),
    messageVars: (m) => ({ reward: rewardLabel(m) }),
  },
  exchange_cancelled: {
    titleKey: 'notifications.events.exchange_cancelled.title',
    messageKey: 'notifications.events.exchange_cancelled.message',
    titleVars: () => ({}),
    messageVars: (m) => ({ points: n(m, 'points_amount') }),
  },

  /* ─── Welcome / seeded notifications ─── */
  foundation_ready: {
    titleKey: 'notifications.events.foundation_ready.title',
    messageKey: 'notifications.events.foundation_ready.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  business_workspace_active: {
    titleKey: 'notifications.events.business_workspace_active.title',
    messageKey: 'notifications.events.business_workspace_active.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  agent_profile_linked: {
    titleKey: 'notifications.events.agent_profile_linked.title',
    messageKey: 'notifications.events.agent_profile_linked.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  empty_demo_account_ready: {
    titleKey: 'notifications.events.empty_demo_account_ready.title',
    messageKey: 'notifications.events.empty_demo_account_ready.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },

  /* ─── Legal documents ─── */
  legal_document_updated: {
    titleKey: 'notifications.events.legal_document_updated.title',
    messageKey: 'notifications.events.legal_document_updated.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },

  /* ─── Sync ─── */
  sync_job_failed: {
    titleKey: 'notifications.events.sync_job_failed.title',
    messageKey: 'notifications.events.sync_job_failed.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
  sync_job_dead_lettered: {
    titleKey: 'notifications.events.sync_job_dead_lettered.title',
    messageKey: 'notifications.events.sync_job_dead_lettered.message',
    titleVars: () => ({}),
    messageVars: () => ({}),
  },
}

/**
 * Returns a localized title and message for a notification.
 * Falls back to the raw backend title/message for unknown event types.
 */
export function getLocalizedNotification(
  item: AppNotificationRecord,
  t: TFunction
): LocalizedNotification {
  const event = (item.metadata?.event as string) ?? ''
  const def = contentMap[event]

  if (!def) {
    return { title: item.title, message: item.message }
  }

  const titleVars = def.titleVars?.(item.metadata ?? {}) ?? {}
  const messageVars = def.messageVars?.(item.metadata ?? {}) ?? {}
  const resolvedTitleKey =
    typeof def.titleKey === 'function'
      ? def.titleKey(item.metadata ?? {})
      : def.titleKey
  const resolvedMessageKey =
    typeof def.messageKey === 'function'
      ? def.messageKey(item.metadata ?? {})
      : def.messageKey

  return {
    title: t(resolvedTitleKey, { defaultValue: item.title, ...titleVars }),
    message: t(resolvedMessageKey, { defaultValue: item.message, ...messageVars }),
  }
}
